/**
 * The cookies listed under each category in the preferences panel.
 *
 * Read from the public cookie-declaration Edge Function, the same register the
 * cookie declaration widget shows, so the banner and the customer's cookie
 * notice never disagree. Fetched only when a visitor opens the preferences
 * panel, and at most once per page: most visitors never open it, so the
 * banner itself costs nothing extra.
 *
 * Cookies the register has not classified yet ("unknown") are left out. They
 * belong to no category the visitor can switch, and listing them under a
 * heading such as "Unclassified" would show visitors the customer's backlog.
 */

/** One cookie as the preferences panel lists it. */
export interface ListedCookie {
  name: string;
  provider: string | null;
  /** Domain the cookie is set on, without a leading dot. Empty when not known. */
  cookieDomain: string;
  thirdParty: boolean;
  duration: string | null;
  durationSeconds: number | null;
}

/** Listed cookies by banner category key (strictly_necessary, analytics, ...). */
export type CookieListByCategory = Record<string, ListedCookie[]>;

const LISTED_CATEGORIES = ["strictly_necessary", "analytics", "advertising", "functional", "social"] as const;

/** The most cookies listed under one category, so a hostile payload cannot flood the panel. */
export const MAX_COOKIES_PER_CATEGORY = 200;

const str = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value.slice(0, 300) : null);

/** Keeps only well-formed entries of the categories the banner offers. */
export function parseCookieList(body: unknown): CookieListByCategory {
  const out: CookieListByCategory = {};
  const categories = (body as { categories?: unknown } | null)?.categories;
  if (!categories || typeof categories !== "object") return out;
  for (const category of LISTED_CATEGORIES) {
    const entries = (categories as Record<string, unknown>)[category];
    if (!Array.isArray(entries)) continue;
    const listed: ListedCookie[] = [];
    for (const entry of entries.slice(0, MAX_COOKIES_PER_CATEGORY)) {
      if (!entry || typeof entry !== "object") continue;
      const e = entry as Record<string, unknown>;
      const name = str(e["name"]);
      if (!name) continue;
      const seconds = e["duration_seconds"];
      listed.push({
        name,
        provider: str(e["provider"]),
        cookieDomain: str(e["cookie_domain"]) ?? "",
        thirdParty: e["is_third_party"] === true,
        duration: str(e["duration"]),
        durationSeconds: typeof seconds === "number" && Number.isFinite(seconds) ? seconds : null,
      });
    }
    if (listed.length > 0) out[category] = listed;
  }
  return out;
}

export class CookieListClient {
  private pending: Promise<CookieListByCategory | null> | null = null;

  constructor(private apiUrl: string, private domainId: string) {}

  /** The domain's listed cookies, or null if they could not be read. Fetched once. */
  load(): Promise<CookieListByCategory | null> {
    if (!this.pending) {
      this.pending = this.fetchList().then((list) => {
        // A failed read is not cached, so reopening the panel tries again.
        if (list === null) this.pending = null;
        return list;
      });
    }
    return this.pending;
  }

  private async fetchList(): Promise<CookieListByCategory | null> {
    try {
      const res = await fetch(
        `${this.apiUrl}/functions/v1/cookie-declaration?domain_id=${encodeURIComponent(this.domainId)}`,
        { signal: AbortSignal.timeout(5000) },
      );
      if (!res.ok) return null;
      return parseCookieList(await res.json());
    } catch {
      return null;
    }
  }
}
